import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import { Paperclip } from 'phosphor-react'
import { InspectionService } from 'utility'
import { TicketFileTile } from 'components/TicketFileTile'
import { isImageAttachment } from 'utility/ticket-attachment'
import { useNotification } from 'hooks/toast'
import { MAX_THREAD_FILES } from 'utility/question-threads'

// Only real http(s) links are drawn / opened (URLs come from the server, never javascript: / data:).
const safeUrl = url => (/^https?:\/\//i.test(String(url || '')) ? String(url) : '')

const thumbStyle = size => ({
    position: 'relative',
    display: 'block',
    width: size,
    height: size,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#f8fafc',
    flex: '0 0 auto',
})

const removeStyle = {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 18,
    height: 18,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(15,23,42,0.75)',
    color: '#fff',
    fontSize: 11,
    lineHeight: '18px',
    textAlign: 'center',
    cursor: 'pointer',
}

/** One attachment: pictures as a thumbnail (opens full size in a new tab), other files as a file tile. */
const FileItem = ({ url, file, image, size, onRemove }) => {
    const href = safeUrl(url)
    if (!image || !href) {
        return <TicketFileTile url={href} file={file} size={size} onRemove={onRemove} />
    }
    return (
        <span style={thumbStyle(size)} title={file?.FileName || 'Picture'}>
            <a href={href} target="_blank" rel="noopener noreferrer">
                <img src={href} alt={file?.FileName || 'Attachment'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </a>
            {onRemove && (
                <button type="button" title="Remove" aria-label="Remove attachment" style={removeStyle} onClick={onRemove}>
                    ×
                </button>
            )}
        </span>
    )
}

/** Files of a thread message (normalised: { FileId, FileName, FileType, Url, IsImage }). */
export const ThreadFiles = ({ files, size = 56 }) => {
    const list = (files || []).filter(f => f && (f.Url || f.FileName))
    if (!list.length) return null
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {list.map((f, i) => (
                <FileItem key={f.FileId || `${f.Url}-${i}`} url={f.Url} file={f} image={f.IsImage} size={size} />
            ))}
        </div>
    )
}

/**
 * "Attach file" (any type, max 10) for a thread message. Each file is uploaded at once with the same call as ticket
 * comments (Inspection/Media/Upload); `files` = [{ FileKey, FileName, FileType, previewUrl }] (controlled).
 */
export const ThreadAttachmentPicker = ({ files, onChange, disabled, onUploadingChange }) => {
    const inputRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const { errorToast } = useNotification()
    const current = files || []
    const mountedRef = useRef(true)
    useEffect(
        () => () => {
            mountedRef.current = false
        },
        [],
    )

    const setBusy = value => {
        if (!mountedRef.current) return
        setUploading(value)
        if (onUploadingChange) onUploadingChange(value)
    }

    const uploadOne = async file => {
        const result = await InspectionService.uploadMedia({ media: file })
        const fileKey = result?.CloudImageId || result?.FileKey || ''
        if (!fileKey) throw new Error('no file key')
        return {
            FileKey: fileKey,
            FileName: (file.name || '').slice(0, 200),
            FileType: file.type || 'application/octet-stream',
            previewUrl: result?.ImagePathUrl || result?.FileURL || result?.SignedURL || '',
        }
    }

    const handleFiles = async fileList => {
        const picked = Array.from(fileList || [])
        if (inputRef.current) inputRef.current.value = ''
        if (!picked.length) return
        const room = MAX_THREAD_FILES - current.length
        if (room <= 0) {
            errorToast(`Attach at most ${MAX_THREAD_FILES} files.`)
            return
        }
        if (picked.length > room) errorToast(`Only ${room} more file(s) can be attached (max ${MAX_THREAD_FILES}).`)
        const batch = picked.slice(0, room)
        setBusy(true)
        try {
            const results = await Promise.all(
                batch.map(file =>
                    uploadOne(file).catch(() => {
                        errorToast(`Could not upload ${file.name || 'the file'}. Try again.`)
                        return null
                    }),
                ),
            )
            const uploaded = results.filter(Boolean)
            // functional update: files removed / added while this upload ran are kept
            if (uploaded.length && mountedRef.current) onChange(prev => [...(prev || []), ...uploaded])
        } finally {
            setBusy(false)
        }
    }

    return (
        <div>
            <Button
                type="button"
                size="small"
                basic
                disabled={disabled || uploading || current.length >= MAX_THREAD_FILES}
                loading={uploading}
                onClick={() => inputRef.current?.click()}
                style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}
            >
                <Paperclip size={14} style={{ marginRight: 4, flex: '0 0 auto' }} />
                Attach file
            </Button>
            <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
            {current.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {current.map((f, i) => (
                        <FileItem
                            key={`${f.FileKey}-${i}`}
                            url={f.previewUrl}
                            file={f}
                            image={isImageAttachment({ FileType: f.FileType, FileName: f.FileName })}
                            size={56}
                            onRemove={disabled ? undefined : () => onChange(prev => (prev || []).filter((_, idx) => idx !== i))}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
